import * as React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

interface AnalysisDialogProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	status: 'uploading' | 'analyzing' | null;
}

export function AnalysisDialog({
	isOpen,
	onOpenChange,
	status,
}: AnalysisDialogProps) {
	const statusContent = {
		uploading: {
			emoji: '⚡',
			title: 'File Uploading...',
			description: 'Uploading your document',
		},
		analyzing: {
			emoji: '⚡',
			title: 'Analyzing Brand Voice...',
			description: 'Discovering your unique writing style',
		},
	};

	const content = status ? statusContent[status] : statusContent.uploading;

	// Allow dialog to be closed
	const handleOpenChange = (open: boolean) => {
		onOpenChange(open);
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className='sm:max-w-md [&>button]:hidden'>
				<DialogTitle className='sr-only'>{content.title}</DialogTitle>
				<div className='flex flex-col items-center justify-center py-8 space-y-4'>
					<img
						src='/logo.png'
						alt='Prometheus'
						className='h-12 mb-4'
					/>
					<motion.div
						animate={{
							scale: [1, 1.2, 1],
							rotate: [0, 360],
						}}
						transition={{
							duration: 2,
							repeat: Infinity,
							ease: 'easeInOut',
						}}
						className='text-6xl'
					>
						{content.emoji}
					</motion.div>
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						className='text-xl font-semibold text-purple-500'
					>
						{content.title}
					</motion.div>
					<div className='flex items-center gap-2'>
						<Loader2 className='h-4 w-4 animate-spin text-purple-500' />
						<span className='text-sm text-gray-500'>
							This may take a moment
						</span>
					</div>
					<motion.div
						animate={{
							opacity: [0.5, 1, 0.5],
						}}
						transition={{
							duration: 2,
							repeat: Infinity,
							ease: 'easeInOut',
						}}
						className='text-sm text-gray-400'
					>
						✨ {content.description} ✨
					</motion.div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
